'use client';

import React, { useEffect, useRef, useState } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import {
  UnifiedHistoryEntry,
  clearUnifiedHistory,
  deleteUnifiedHistoryEntry,
  getUnifiedHistory,
} from '@/lib/unified-history';

// Header (53) + filter (52) + ~4.5 rows at 52 each: deliberately lands
// mid-row so a longer list visibly hints that it scrolls.
const EXPANDED_WIDTH = 380;
const EXPANDED_HEIGHT = 344;

function formatRelativeTime(timestamp: number, languageCode: string): string {
  const diffMs = timestamp - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  let rtf: Intl.RelativeTimeFormat;
  try {
    rtf = new Intl.RelativeTimeFormat(languageCode, { numeric: 'auto' });
  } catch {
    rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  }
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  if (Math.abs(diffDay) < 7) return rtf.format(diffDay, 'day');
  return rtf.format(Math.round(diffDay / 7), 'week');
}

interface ResumeHistoryChipProps {
  resumeCandidate: { transcriptId: string; turnIndex: number; label: string };
  locale: LocaleDetails;
  backLabel: string;
  clearHistoryLabel: string;
  completedLabel: string;
  emptyLabel: string;
  searchPlaceholder: string;
  /** Resumes `resumeCandidate` itself — the pill's own click target, unchanged from today. */
  onResumeMain: () => void;
  /** Resumes a different chat surfaced in the history list. */
  onResumeOther: (transcriptId: string, turnIndex: number) => void;
  /** Dismisses the resume suggestion forever (the small X) — unchanged from today. */
  onDismiss: () => void;
  /** Re-populates the search bar with a past plain-search query. */
  onSelectSearch: (searchQuery: string) => void;
}

export function ResumeHistoryChip({
  resumeCandidate,
  locale,
  backLabel,
  clearHistoryLabel,
  completedLabel,
  emptyLabel,
  searchPlaceholder,
  onResumeMain,
  onResumeOther,
  onDismiss,
  onSelectSearch,
}: ResumeHistoryChipProps) {
  // Keyed off the `locale` PROP, so these follow a mid-conversation language
  // flip exactly like the rest of the search surface.
  const historyContent = getSearchUiContent(locale);
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<UnifiedHistoryEntry[]>([]);
  const [filter, setFilter] = useState('');
  const [pinnedSize, setPinnedSize] = useState<{ width: number; height: number } | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const backBtnRef = useRef<HTMLButtonElement>(null);

  // The collapsed chip is left at its NATURAL size (no inline width/height) —
  // it is only pinned to pixels for the duration of an open/close cycle.
  //
  // CSS can't transition width/height from `auto`, so a concrete "from" value
  // has to exist before the box starts growing. Measuring at mount to get it
  // does not work: the label is `truncate` + `min-w-0`, so its min-content
  // width is 0, and the composer subtree this chip lives in is still animating
  // when the chip first appears — the reading comes back as ~2px (just the two
  // borders) and pins the chip as a permanent sliver. Measuring on the open
  // click instead means layout is always settled and the value is always real.
  const openPanel = () => {
    const el = wrapperRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width >= 1) {
        // Write the measured size straight to the DOM and flush layout here, in
        // this same tick, so the growth has a real "from" value to animate off.
        // Deliberately not deferred behind requestAnimationFrame: rAF does not
        // fire in a backgrounded tab, which would leave a click that lands just
        // before a tab switch permanently un-opened.
        el.style.width = `${rect.width}px`;
        el.style.height = `${rect.height}px`;
        void el.offsetWidth;
        setPinnedSize({ width: rect.width, height: rect.height });
      }
    }
    setOpen(true);
  };

  // Release the pin once the collapse has finished, returning the chip to
  // natural sizing (so a label change can never be measured against a stale
  // pinned width).
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setPinnedSize(null), 360);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setEntries(getUnifiedHistory(locale.regionCode));
    setFilter('');

    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    const focusTimer = setTimeout(() => backBtnRef.current?.focus({ preventScroll: true }), 320);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      clearTimeout(focusTimer);
    };
  }, [open, locale.regionCode]);

  const filtered = filter.trim()
    ? entries.filter((entry) => entry.label.toLowerCase().includes(filter.trim().toLowerCase()))
    : entries;

  const handleEntryClick = (entry: UnifiedHistoryEntry) => {
    if (entry.type === 'chat') {
      onResumeOther(entry.sourceId, entry.lastTurnIndex);
    } else {
      onSelectSearch(entry.label);
    }
    setOpen(false);
  };

  const handleDeleteEntry = (e: React.MouseEvent, entry: UnifiedHistoryEntry) => {
    e.stopPropagation();
    deleteUnifiedHistoryEntry(entry);
    setEntries((prev) => prev.filter((x) => x !== entry));
  };

  return (
    <div
      ref={wrapperRef}
      // Same surface as the composer bar itself (see text-area-search-bar.tsx's
      // capsule): identical border, background and shadow classes, so the chip
      // reads as the same material rather than a separate floating widget.
      className={`history-morph max-w-[85%] border border-input bg-canvas shadow-search-bar dark:border-transparent-dark dark:bg-dark dark:shadow-search-bar-dark ${
        open ? 'is-open rounded-2xl' : 'rounded-full'
      }`}
      style={{
        width: open ? EXPANDED_WIDTH : (pinnedSize?.width ?? undefined),
        height: open ? EXPANDED_HEIGHT : (pinnedSize?.height ?? undefined),
      }}
    >
      <div className="history-morph-face-pill">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onResumeMain}
          className="group flex min-w-0 items-center gap-2.5 py-2.5 pl-4 pr-1 text-left"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-500 dark:group-hover:text-zinc-400 transition-colors"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 14 4 9l5-5" />
            <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11" />
          </svg>
          <span className="min-w-0 truncate text-sm leading-none text-zinc-600 dark:text-zinc-300">
            {resumeCandidate.label}
          </span>
        </button>
        <span className="h-5 w-px flex-shrink-0 bg-zinc-200 dark:bg-white/15" />
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={historyContent.historySeeAll ?? 'See all history'}
          title={historyContent.historySeeAll ?? 'See all history'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={openPanel}
          className="flex-shrink-0 rounded-full p-1.5 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-white/10 transition-all duration-150"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v4.5l3 2" />
          </svg>
        </button>
        <button
          type="button"
          aria-label={historyContent.historyDismiss ?? 'Dismiss'}
          title={historyContent.historyDismiss ?? 'Dismiss'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onDismiss}
          className="mr-2 flex-shrink-0 rounded-full p-1.5 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-white/10 transition-all duration-150"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="history-morph-face-panel flex h-full flex-col">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-zinc-100 dark:border-white/10 px-2.5 py-2.5">
          <button
            ref={backBtnRef}
            type="button"
            onClick={() => {
              setOpen(false);
              triggerRef.current?.focus({ preventScroll: true });
            }}
            className="flex items-center gap-1.5 rounded-lg py-1.5 pl-1.5 pr-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-white/10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 6-6 6 6 6" />
            </svg>
            {backLabel}
          </button>
          {entries.length > 0 && (
            <button
              type="button"
              onClick={() => {
                clearUnifiedHistory(locale.regionCode);
                setEntries([]);
              }}
              className="rounded-lg px-2 py-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              {clearHistoryLabel}
            </button>
          )}
        </div>

        <div className="relative flex-shrink-0 px-3 py-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="pointer-events-none absolute left-[22px] top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 w-full rounded-full border border-zinc-200/70 dark:border-white/10 bg-zinc-50 dark:bg-white/5 pl-9 pr-3 text-sm text-zinc-700 dark:text-zinc-200 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {filtered.length === 0 ? (
            <div className="px-2 py-5 text-center text-sm text-zinc-400 dark:text-zinc-500">{emptyLabel}</div>
          ) : (
            filtered.map((entry) => (
              <div
                key={`${entry.type}-${entry.sourceId}`}
                role="button"
                tabIndex={0}
                onClick={() => handleEntryClick(entry)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEntryClick(entry);
                  }
                }}
                className="group flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-zinc-50 dark:hover:bg-white/5 cursor-pointer"
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-zinc-100 dark:bg-white/10 text-zinc-500 dark:text-zinc-400">
                  {entry.type === 'chat' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 14 4 9l5-5" />
                      <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7.5v4.5l3 2" />
                    </svg>
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-zinc-700 dark:text-zinc-200">{entry.label}</span>
                  <span className="flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-500">
                    {formatRelativeTime(entry.timestamp, locale.languageCode)}
                    {entry.type === 'chat' && entry.completed && (
                      <>
                        <span className="h-0.5 w-0.5 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                        {completedLabel}
                      </>
                    )}
                  </span>
                </span>
                <span className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={historyContent.historyDelete ?? 'Delete'}
                    onClick={(e) => handleDeleteEntry(e, entry)}
                    className="rounded-full p-1.5 text-zinc-400 dark:text-zinc-500 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 6h16" />
                      <path d="M9 6V4h6v2" />
                      <path d="m6 6 1 14h10l1-14" />
                    </svg>
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-zinc-400 dark:text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ResumeHistoryChip;
